// サーバーと画面の両方で使う小物
export const NONE = 'none';             // 「立場なし（質問・補足）」の立場 ID
export const FOLD_THRESHOLD = 3;        // 通報の重みがこの数で畳む
export const DISPUTE_THRESHOLD = 3;     // 前提は、この数の「異議あり」で「争いあり」になる
export const PROPOSAL_THRESHOLD = 5;    // 新しい立場の提案は、この数の賛同で正式な立場になる
export const NEW_VIEW_TARGET = 30;      // この人数に見られるまでは「新着」枠で評価を待つ
export const NEW_LIMIT = 3;             // 新着枠に一度に出す件数
export const VIEW_MS = 1500;            // 画面にこの時間映っていたら「見た」に数える
export const TITLE_MAX = 60;
export const CAMP_MAX = 30;
export const POST_MAX = 1000;           // ひとことの上限（X のように短くはしないが、貼り付けで埋め尽くされない程度）
export const HANDLE_MIN = 2;
export const HANDLE_MAX = 20;
export const SOURCE_MAX = 2;            // 根拠のリンクは2つまで
export const ARENA_AT = 4;              // 元のひとことを含めた往復の書き込みがこの数になったら議論場へ
export const TURN_LIMIT = 24 * 60 * 60 * 1000;      // 持ち時間。自分の番が来てからこの間に返す
export const AWAY_LIMIT = 3 * 24 * 60 * 60 * 1000;  // 「しばらく離れます」で一度だけ延ばせる長さ（番が来てから）

// X のポストのアドレスか（x.com / twitter.com の …/status/数字）。正しければ https://x.com/… の形にそろえて返す
export function normalizeXPost(raw: string): string | null {
  try {
    const u = new URL(raw.trim());
    if (!/^(www\.|mobile\.)?(x|twitter)\.com$/.test(u.hostname)) return null;
    const m = u.pathname.match(/^\/([A-Za-z0-9_]{1,15})\/status(?:es)?\/(\d{5,25})/);
    return m ? `https://x.com/${m[1]}/status/${m[2]}` : null;
  } catch { return null; }
}

// 通報の理由。個人情報の暴露と脅迫は、害がすぐ広がるので立場に関係なく通報でき、少ない数で隠す
export const REPORT_REASONS = {
  abuse: '暴言・人格攻撃', privacy: '個人情報の暴露', threat: '脅迫', discrimination: '差別', spam: '宣伝・荒らし', other: 'その他',
} as const;
export const URGENT_REASONS: string[] = ['privacy', 'threat'];

// 待ってほしい長さの呼び方（3時間・半日・1日・3日）
export const WAIT_HOURS = [3, 12, 24, 72];
export function waitLabel(h: number): string { return h >= 24 ? `${h / 24}日` : h === 12 ? '半日' : `${h}時間`; }

// 残り時間を「1日3時間」「5時間」のように
export function remain(ms: number): string {
  const h = Math.max(0, Math.ceil(ms / (60 * 60 * 1000)));
  return h >= 24 ? `${Math.floor(h / 24)}日${h % 24 ? `${h % 24}時間` : ''}` : `${h}時間`;
}

// 根拠のリンクは、どこが出どころか分かるようにサイト名（ドメイン）で見せる
export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

export const MIN = 60 * 1000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;

export function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
export function fnv(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
export function ago(t: number, now = Date.now()): string {
  const m = Math.floor((now - t) / MIN);
  if (m < 1) return 'たった今';
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  return `${Math.floor(h / 24)}日前`;
}
// 日本時間で表示する（サーバーは UTC で動くため）
export function dateLabel(t: number): string {
  const d = new Date(t + 9 * HOUR);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${d.getUTCHours()}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
export function cut(s: string, n: number): string { return [...s].length > n ? [...s].slice(0, n).join('') + '…' : s; }
export function norm(s: string): string { return String(s).toLowerCase().replace(/[\s#＃、。・？?！!「」]/g, ''); }

// 見る人ごとに決まる並べ替え。サーバーで作った画面と、画面側で作り直した画面が同じ並びになるよう、乱数は使わない
export function seededOrder<T>(items: T[], seed: string, key: (x: T) => string): T[] {
  return [...items].sort((a, b) => fnv(seed + key(a)) - fnv(seed + key(b)));
}

// 用意しておくタグと、その読み（五十音順に並べるため）
export const PRESET_TAGS: [string, string][] = [
  ['AI', 'えーあい'], ['SNS', 'えすえぬえす'], ['アニメ・漫画', 'あにめ'], ['医療', 'いりょう'], ['お金', 'おかね'],
  ['家族', 'かぞく'], ['学校', 'がっこう'], ['環境', 'かんきょう'], ['規制', 'きせい'], ['教育', 'きょういく'],
  ['ゲーム', 'げーむ'], ['芸能', 'げいのう'], ['結婚', 'けっこん'], ['健康', 'けんこう'], ['子ども', 'こども'],
  ['仕事', 'しごと'], ['社会', 'しゃかい'], ['趣味', 'しゅみ'], ['食べ物', 'たべもの'], ['スポーツ', 'すぽーつ'],
  ['政治', 'せいじ'], ['制度', 'せいど'], ['創作', 'そうさく'], ['地域', 'ちいき'], ['テクノロジー', 'てくのろじー'],
  ['働き方', 'はたらきかた'], ['法律', 'ほうりつ'], ['マナー', 'まなー'], ['恋愛', 'れんあい'],
];
export const TAG_READING: Record<string, string> = Object.fromEntries(PRESET_TAGS);
export function sortTags(tags: string[]): string[] {
  return [...tags].sort((a, b) => (TAG_READING[a] || a).localeCompare(TAG_READING[b] || b, 'ja'));
}
export function parseTags(s: string): string[] {
  return [...new Set(s.split(/[\s,、#＃]+/).map(x => x.trim()).filter(Boolean))].map(t => cut(t, 20)).slice(0, 5);
}

// それなの率。見た人が少ないうちは控えめに評価する（Wilson スコアの下限）
export const NARUHODO_WEIGHT = 2;   // おすすめの並びで、反対の立場からの「なるほど」1つを「それな」いくつ分に数えるか
export function score(likes: number, views: number): number {
  const n = Math.max(views, likes), k = likes;
  if (!n) return 0;
  const z = 1.64, r = k / n;
  return (r + z * z / (2 * n) - z * Math.sqrt((r * (1 - r) + z * z / (4 * n)) / n)) / (1 + z * z / n);
}

// 似た話題を探すための2文字ずつの組
export function bigrams(s: string): Set<string> {
  const t = norm(s).replace(/(べきか|べき|ですか|ますか|のか|か)$/, '');
  const out = new Set<string>();
  for (let i = 0; i < t.length - 1; i++) out.add(t.slice(i, i + 2));
  return out;
}
export function similarity(title: string, tags: string[], other: { title: string; tags: string[] }): number {
  const a = bigrams(title), b = bigrams(other.title);
  let shared = 0;
  for (const x of a) if (b.has(x)) shared++;
  const tagHit = other.tags.filter(t => tags.includes(t)).length;
  return shared / Math.max(1, Math.min(a.size, b.size)) + tagHit * 0.15;
}
